/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Which destination-shaped skeleton (if any) to show during a blocking navigation.
 * `null` means "no skeleton" — the shopper only sees the top progress bar.
 */
export type NavSkeletonKind = 'pdp' | 'grid' | null;

// A PDP destination is either the internal `/product/:id` link OR a legacy SEO URL, which the
// `_app.$slug.$productId[.html]` route serves as `/<slug>/<productId>.html` (two path segments,
// last ending in `.html`). Content/blog pages use the single-segment `/<id>.html` shape, so the
// two-segment requirement keeps them out. (baggallini is single-locale, so paths carry no
// site/locale prefix that would add segments — a content page can't masquerade as a PDP here.)
const PDP_SEO_PATH = /^\/[^/]+\/[^/]+\.html$/;

/**
 * Map a pending destination path to the skeleton that matches its layout.
 *
 * This is an explicit **allowlist of top-of-funnel routes** that (a) block on their loader and
 * (b) have a stable, known layout worth previewing. Everything else — cart, account, checkout,
 * the homepage (merchant-authored PD content with variable shape), and all `.html` content
 * pages — returns `null` so the shared `_app` layout never paints a wrong-shaped skeleton over
 * an unrelated route. Add a route here only when both conditions hold.
 */
export function matchNavSkeletonKind(pathname: string): NavSkeletonKind {
    if (pathname.startsWith('/product/') || PDP_SEO_PATH.test(pathname)) {
        return 'pdp';
    }
    if (pathname === '/search' || pathname.startsWith('/category/')) {
        return 'grid';
    }
    return null;
}
